import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, Typography, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhoneVideo from '../assets/img/phone.mp4';
import AppStoreBadge from '../assets/img/appstore.png';
import PlayStoreBadge from '../assets/img/playstore.png';
import useStyles from '../styles/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { FONT_FAMILY } from 'common/sharedFunctions';
import { QRCodeCanvas } from 'qrcode.react';

const DownloadApp = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector(state => state.settingsdata.settings);
  const { t, i18n } = useTranslation();
  
  // Memoïser lang
  const lang = useMemo(() => {
    const rawLang = i18n?.language || i18n?.languages?.[0] || 'en';
    return rawLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  }, [i18n?.language, i18n?.languages]);

  const localTranslations = useMemo(() => ({
    en: {
      mobile_apps_on_store: 'Download our app',
      app_store_deception1: "Scan the QR code or download from your app store to book your next ride with Allô Driver.",
    },
    fr: {
      mobile_apps_on_store: 'Téléchargez notre application',
      app_store_deception1: "Scannez le QR code ou téléchargez depuis votre store pour réserver votre prochaine course avec Allô Driver.",
    }
  }), []);

  const localT = useCallback((key) => {
    try {
      const v = t(key);
      if (v && v !== key) return v;
    } catch (e) {}
    return localTranslations[lang]?.[key] || localTranslations.en[key] || '';
  }, [t, localTranslations, lang]);
  const isRTL = i18n.dir();

  // animate image and badges on scroll into view
  const imgRef = useRef(null);
  const badgesRef = useRef(null);
  const [imgVisible, setImgVisible] = useState(false);
  const [badgesVisible, setBadgesVisible] = useState(false);

  useEffect(() => {
    const observers = [];
    if (imgRef.current) {
      const obsImg = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) setImgVisible(true); });
      }, { threshold: 0.15 });
      obsImg.observe(imgRef.current);
      observers.push(obsImg);
    }
    if (badgesRef.current) {
      const obsBadges = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) setBadgesVisible(true); });
      }, { threshold: 0.15 });
      obsBadges.observe(badgesRef.current);
      observers.push(obsBadges);
    }
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <Box className={classes.aboutUsContainer} style={{marginBottom: -15, backgroundColor: '#000'}}>

      <Grid container spacing={0} className={classes.gridContainer} style={{ margin: 0, width: '100%' }}>
        {/* two-column row: video left, text right */}
        <Grid item xs={12} md={6}>
          <div
            ref={imgRef}
            className={`${classes.glassCard} ${imgVisible ? classes.buttonRowVisible : classes.buttonRow}`}
            style={{
              width: '100%',
              maxWidth: isMobile ? 300 : 420,
              margin: '0 auto',
              borderRadius: 16,
              overflow: 'hidden',
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              marginRight: isRTL === 'rtl' ? 0 : (isMobile ? 0 : 24),
              marginLeft: isRTL === 'rtl' ? (isMobile ? 0 : 24) : 0,
              marginBottom: isMobile ? 24 : 0
            }}
          >
            <video
              src={PhoneVideo}
              className={classes.largeImage}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
            />
          </div>
        </Grid>

        <Grid item xs={12} md={6} style={{
          display:'flex', 
          flexDirection:'column', 
          justifyContent:'center',
          textAlign: isMobile ? 'center' : 'left',
          paddingLeft: isMobile ? '16px' : '0',
          paddingRight: isMobile ? '16px' : '0'
        }}>
          <div>
            <Typography variant="h3" fontWeight={700} fontFamily={FONT_FAMILY} className={classes.titleMain} style={{
              color:'#fff',
              textAlign: 'center',
              fontSize: isMobile ? 'clamp(24px, 7vw, 32px)' : 'clamp(28px, 6vw, 48px)',
              marginBottom: isMobile ? '16px' : '20px'
            }}>
              {localT('mobile_apps_on_store')}
            </Typography>
            <Typography fontFamily={FONT_FAMILY} className={classes.subtitleMain} style={{
              color:'rgba(255,255,255,0.92)',
              textAlign: 'center',
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: 1.6,
              marginBottom: isMobile ? '24px' : '20px',
              whiteSpace: isMobile ? 'pre-line' : 'normal'
            }}>
              {isMobile ? localT('app_store_deception1').replace(' — ', '\n— ') : localT('app_store_deception1')}
            </Typography>

            <div
              ref={badgesRef}
              className={`${classes.buttonRow} ${badgesVisible ? classes.buttonRowVisible : ''}`}
              style={{ 
                marginTop: 24, 
                flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row',
                justifyContent: 'center',
                gap: isMobile ? 16 : 32,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              {/* QR Codes Section */}
              {!isMobile && (
                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                  {/* iOS QR Code */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      background: 'white', 
                      padding: 12, 
                      borderRadius: 12,
                      boxShadow: '0 8px 24px rgba(212, 175, 55, 0.2)',
                      marginBottom: 8
                    }}>
                      <QRCodeCanvas 
                        value={settings && settings.AppleStoreLink ? settings.AppleStoreLink : 'https://apps.apple.com'} 
                        size={120}
                        level="H"
                      />
                    </div>
                    <Typography style={{ color: '#d4af37', fontSize: 13, fontWeight: 600 }}>
                      iOS App
                    </Typography>
                  </div>

                  {/* Android QR Code */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      background: 'white', 
                      padding: 12, 
                      borderRadius: 12,
                      boxShadow: '0 8px 24px rgba(212, 175, 55, 0.2)',
                      marginBottom: 8
                    }}>
                      <QRCodeCanvas 
                        value={settings && settings.PlayStoreLink ? settings.PlayStoreLink : 'https://play.google.com'} 
                        size={120}
                        level="H"
                      />
                    </div>
                    <Typography style={{ color: '#d4af37', fontSize: 13, fontWeight: 600 }}>
                      Android App
                    </Typography>
                  </div>
                </div>
              )}

              {/* Store Badges */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <a
                  href={settings && settings.AppleStoreLink ? settings.AppleStoreLink : '#'}
                  style={{
                    display:'inline-block', 
                    padding:8, 
                    borderRadius:10, 
                    background:'rgba(255,255,255,0.05)', 
                    boxShadow: '0 6px 18px rgba(0,0,0,0.15)', 
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.15)';
                  }}
                >
                  <img src={AppStoreBadge} alt="Download on App Store" style={{
                    height: isMobile ? 44 : 38, 
                    objectFit:'contain', 
                    display:'block', 
                    width: 'auto'
                  }}/>
                </a>
                <a
                  href={settings && settings.PlayStoreLink ? settings.PlayStoreLink : '#'}
                  style={{
                    display:'inline-block', 
                    padding:8, 
                    borderRadius:10, 
                    background:'rgba(255,255,255,0.05)', 
                    boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.15)';
                  }}
                >
                  <img src={PlayStoreBadge} alt="Get it on Google Play" style={{
                    height: isMobile ? 44 : 38, 
                    objectFit:'contain', 
                    display:'block', 
                    width: 'auto'
                  }}/>
                </a>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(DownloadApp);
